import { createSignal, createEffect, untrack } from "solid-js";
import { useApi, isOk } from "../api";
import Text from "../components/Text";
import MetricHistoryChart from "../components/MetricHistoryChart";
import { DateRangePills, type DateRangeValue } from "../components/DateRangePills";
import type { BotEngineMetricBucketDto } from "../api/generated/schemas";
import styles from "./BotEngineHistory.module.css";

function rangeToFrom(range: DateRangeValue): string | undefined {
    const now = new Date();
    switch (range) {
        case "today": {
            const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            return start.toISOString();
        }
        case "7d":
            return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
        case "30d":
            return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
        default:
            // "all" — the backend clamps this to its own retention-aligned max range.
            return new Date(0).toISOString();
    }
}

export default function BotEngineHistory() {
    const api = useApi();

    const [range, setRange] = createSignal<DateRangeValue>("7d");
    const [buckets, setBuckets] = createSignal<BotEngineMetricBucketDto[]>([]);
    const [loading, setLoading] = createSignal(true);

    async function fetchHistory(r: DateRangeValue) {
        setLoading(true);
        try {
            const from = rangeToFrom(r);
            const to = new Date().toISOString();
            const res = await api.botStats.getHistory({ from, to });
            if (isOk(res.data)) setBuckets((res.data.value.buckets ?? []) as BotEngineMetricBucketDto[]);
            else setBuckets([]);
        } finally {
            setLoading(false);
        }
    }

    createEffect(() => {
        const r = range();
        untrack(() => void fetchHistory(r));
    });

    return (
        <div class={styles.container}>
            <div class={styles.header}>
                <h1><Text val="BOT_HISTORY_TITLE" /></h1>
                <DateRangePills value={range()} onChange={setRange} />
            </div>

            <div class={styles.grid}>
                <MetricHistoryChart
                    titleKey="BOT_HISTORY_BOTS_ONLINE"
                    buckets={buckets()}
                    loading={loading()}
                    value={(b) => b.botsOnline}
                    color="#4caf9f"
                />
                <MetricHistoryChart
                    titleKey="BOT_HISTORY_RECONNECTS"
                    buckets={buckets()}
                    loading={loading()}
                    value={(b) => b.reconnects}
                    color="#e0a13c"
                />
                <MetricHistoryChart
                    titleKey="BOT_HISTORY_QUEUE_DEPTH"
                    buckets={buckets()}
                    loading={loading()}
                    value={(b) => b.queueDepth}
                    color="#7c4dff"
                />
                <MetricHistoryChart
                    titleKey="BOT_HISTORY_AUTOMATION_QUEUE"
                    buckets={buckets()}
                    loading={loading()}
                    value={(b) => b.automationQueueDepth}
                    color="#f9a825"
                />
                <MetricHistoryChart
                    titleKey="BOT_HISTORY_AUTOMATION_RATE"
                    buckets={buckets()}
                    loading={loading()}
                    value={(b) => b.automationEnqueuesPerSecond}
                    color="#26a69a"
                    decimals={2}
                />
                <MetricHistoryChart
                    titleKey="BOT_HISTORY_AUTOMATION_DROPS"
                    buckets={buckets()}
                    loading={loading()}
                    value={(b) => b.automationDropsTotal}
                    color="#ef5350"
                />
                <MetricHistoryChart
                    titleKey="BOT_HISTORY_TPS"
                    buckets={buckets()}
                    loading={loading()}
                    value={(b) => b.tps}
                    color="#4d9bff"
                    decimals={1}
                />
                <MetricHistoryChart
                    titleKey="BOT_HISTORY_PACKETS_PER_SECOND"
                    buckets={buckets()}
                    loading={loading()}
                    value={(b) => b.packetsPerSecond}
                    color="#ff6b9d"
                    decimals={1}
                />
                <MetricHistoryChart
                    titleKey="BOT_HISTORY_AVG_TICK_MS"
                    buckets={buckets()}
                    loading={loading()}
                    value={(b) => b.avgTickMs}
                    color="#ff8a4d"
                    unit=" ms"
                    decimals={1}
                />
            </div>
        </div>
    );
}
